import type { CostOfGoodsResource } from '@repo/api-client';

/**
 * What the goods cost, and what that left — read off the one resource in this
 * app that carries either.
 *
 * ## Why this is a module rather than a ternary in the view
 *
 * `CostOfGoodsResource` is a discriminated union wearing three separate fields:
 * `applicability` says whether there is a cost at all, and the two figures are
 * `number | null` and are null unless it is `APPLICABLE`. The obvious rendering
 * — `formatPaise(costOfGoods.costOfGoodsSoldPaise ?? 0)` — typechecks, renders
 * beautifully and is a lie about the park's buying position:
 *
 * > **APPLICABLE** — goods were sold and both figures below are present, a cost
 * > of 0 included; **NOT_APPLICABLE** — no goods were sold, so there is no cost
 * > of goods and both figures are null. Never collapse the second into a cost of
 * > zero: zero says the park sold goods at full margin, and not-applicable says
 * > it sold none.
 *
 * Those are the document's own words. ₹0.00 in the cost column of a profit and
 * loss is a claim — that the park sold goods which cost it nothing — and it is
 * the claim an owner would act on hardest. So the branch is made once, here,
 * where it can be read and tested, and the view renders the answer.
 *
 * ## The third answer, and why it exists
 *
 * The types permit `APPLICABLE` with a null figure, and `NOT_APPLICABLE` with a
 * figure present. Neither is supposed to happen and the document says as much —
 * but the document is not the wire, and a screen that reached for `?? 0` in that
 * case would publish a number the API never sent, on the most sensitive read in
 * the system, with nothing to say it had done so.
 *
 * {@link costLines} therefore answers `inconsistent` and the screen says
 * plainly that the figure is not shown. That is the same instinct the rest of
 * this epic follows: `netPaise` is rendered as sent even when the terms above it
 * do not add up to it, because a screen that quietly agrees with its own
 * arithmetic is the one place a contract defect can never be found.
 */

/** What there is to show for cost and margin. One value, so two cannot coexist. */
export type CostLines =
  | {
      readonly kind: 'figures';
      readonly costOfGoodsSoldPaise: number;
      readonly grossMarginPaise: number;
    }
  /** No goods were sold in the period. **Not** a cost of zero. */
  | { readonly kind: 'no-goods' }
  /** The discriminant and the figures disagree. Nothing is invented to fill it. */
  | { readonly kind: 'inconsistent' };

/**
 * The cost and margin a statement should show, decided from all three fields
 * rather than from the discriminant alone.
 *
 * A cost of `0` and a margin of `0` are ordinary values and pass through — the
 * check is `typeof … === 'number'` and never a truthiness test, which is the
 * shape that would silently turn "sold at full margin" into "sold nothing".
 */
export function costLines(costOfGoods: CostOfGoodsResource): CostLines {
  const { applicability, costOfGoodsSoldPaise, grossMarginPaise } = costOfGoods;

  const bothPresent =
    typeof costOfGoodsSoldPaise === 'number' && typeof grossMarginPaise === 'number';

  if (applicability === 'APPLICABLE') {
    return bothPresent
      ? { kind: 'figures', costOfGoodsSoldPaise, grossMarginPaise }
      : { kind: 'inconsistent' };
  }

  const bothAbsent = costOfGoodsSoldPaise === null && grossMarginPaise === null;

  return bothAbsent ? { kind: 'no-goods' } : { kind: 'inconsistent' };
}
